<template>
  <div id="app">
    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">📱</span>
          </div>
          <h1 class="dashboard-title">SIM Control Dashboard</h1>
        </div>
        <div class="header-controls">
          <div class="live-indicator">
            <div :class="['live-dot', { 'loading': loading }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ loading ? 'Loading...' : 'Live' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- API Notification -->
    <div v-if="apiNotification" :class="[
      'api-notification',
      'notification-' + apiNotification.type
    ]">
      <div class="notification-content">
        <p class="notification-message">{{ apiNotification.message }}</p>
        <button @click="apiNotification = null" class="notification-close">×</button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Statistics Overview</h3>
        </div>

        <div class="stats-wrapper">
          <div class="stats-container">
            <div class="stat-card">
              <div class="stat-label">Total SIMs</div>
              <div class="stat-value">{{ stats.total }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">✓</span>
                <span>{{ stats.active }} Active, {{ stats.suspended }} Suspended</span>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-label">Low Data SIMs</div>
              <div class="stat-value">{{ stats.criticalAlerts }}</div>
              <div class="stat-change negative">
                <span class="stat-icon">⚠</span>
                <span>{{ stats.criticalData }} Critical, {{ stats.warningAlerts }} Warning</span>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-label">Total Data Used (MB)</div>
              <div class="stat-value">{{ stats.totalDataUsedMb.toFixed(1) }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">💧</span>
                <span>{{ stats.totalDatadata_balance_in_mb.toFixed(1) }} MB remaining</span>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-label">Data Usage Change</div>
              <div class="stat-value">{{ Math.abs(comparisonData.percentageUsed).toFixed(1) }}%</div>
              <div class="stat-change" :class="comparisonData.percentageUsed > 0 ? 'negative' : 'positive'">
                <span class="stat-icon">{{ comparisonData.percentageUsed > 0 ? '📉' : '📈' }}</span>
                <span>{{ comparisonData.dataUsed.toFixed(1) }} MB consumed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Comparison Section -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">Data Usage Comparison</div>
        </div>

        <div class="comparison-content">
          <div class="comparison-grid">
            <div class="comparison-card">
              <h4 class="comparison-title">Previous Data</h4>
              <div class="comparison-detail">
                <span class="detail-label">Total Bundle:</span>
                <span class="detail-value">{{ comparisonData.previousTotal.toFixed(1) }} MB</span>
              </div>
              <div class="comparison-detail">
                <span class="detail-label">Remaining:</span>
                <span class="detail-value primary">{{ comparisonData.previousRemaining.toFixed(1) }} MB</span>
              </div>
            </div>

            <div class="comparison-card">
              <h4 class="comparison-title">Current Data</h4>
              <div class="comparison-detail">
                <span class="detail-label">Total Bundle:</span>
                <span class="detail-value">{{ comparisonData.currentTotal.toFixed(1) }} MB</span>
              </div>
              <div class="comparison-detail">
                <span class="detail-label">Remaining:</span>
                <span class="detail-value primary">{{ comparisonData.currentRemaining.toFixed(1) }} MB</span>
              </div>
            </div>

            <div class="comparison-card highlight">
              <h4 class="comparison-title">Data Consumed</h4>
              <div class="comparison-detail">
                <span class="detail-label">Usage:</span>
                <span class="detail-value accent">{{ comparisonData.dataUsed.toFixed(1) }} MB</span>
              </div>
              <div class="comparison-detail">
                <span class="detail-label">Change:</span>
                <span class="detail-value accent">{{ Math.abs(comparisonData.percentageUsed).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <div class="chart-card">
          <div class="chart-header">
            <h3 class="chart-title">Data Distribution</h3>
          </div>
          <div class="chart-body">
            <canvas ref="dataDistributionChart" width="300" height="300"></canvas>
          </div>
          <div class="chart-legend">
            <div class="legend-item">
              <div class="legend-dot critical"></div>
              <span>Critical (0-10MB)</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot warning"></div>
              <span>Low (10-50MB)</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot medium"></div>
              <span>Medium (50-100MB)</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot good"></div>
              <span>Good (>100MB)</span>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-header">
            <h3 class="chart-title">SIM Status</h3>
          </div>
          <div class="chart-body">
            <canvas ref="statusChart" width="300" height="300"></canvas>
          </div>
          <div class="chart-legend">
            <div class="legend-item">
              <div class="legend-dot active"></div>
              <span>{{ stats.active }} Active</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot suspended"></div>
              <span>{{ stats.suspended }} Suspended</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot inactive"></div>
              <span>{{ stats.inactive }} Inactive</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Section -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Filters</h3>
        </div>

        <div class="search-controls-wrapper">
          <div class="search-controls">
            <div class="search-group">
              <label class="search-label">Search SIMs</label>
              <input type="text" class="search-input" v-model="searchQuery" placeholder="Search MSISDN, Network...">
            </div>

            <div class="search-group">
              <label class="search-label">Filter by Status</label>
              <select v-model="filterStatus" class="search-select">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div class="search-group">
              <label class="search-label">Filter by Alert Level</label>
              <select v-model="filterAlertLevel" class="search-select">
                <option value="">All Levels</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="ok">OK</option>
              </select>
            </div>

            <div class="search-group">
              <label class="search-label">Sort by</label>
              <select v-model="sortBy" class="search-select">
                <option value="msisdn">MSISDN</option>
                <option value="data-asc">Data Balance (Low to High)</option>
                <option value="data-desc">Data Balance (High to Low)</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          <div class="filter-actions">
            <button class="apply-filters-btn" @click="loadData">
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      <!-- Data Grid -->
      <div class="grid-container vs-con-loading__container" ref="gridTable">
        <div class="grid-header">
          <div class="grid-title">SIM Details</div>
          <div class="grid-controls">
            <div class="grid-actions">
              <button class="action-button refresh-btn" @click="loadData" :disabled="loading" title="Refresh">
                <span class="btn-icon">🔄</span>
                <span>{{ loading ? 'Loading...' : 'Refresh' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>

          <table v-if="!loading && sortedAndFilteredSims.length > 0" class="data-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>MSISDN</th>
                <th>Status</th>
                <th>Data Balance</th>
                <th>Alert Level</th>
                <th>Network</th>
                <th>Usage (MB)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sim in paginatedSims" :key="sim.id" @click="goToSimControl(sim.id)"
                style="cursor: pointer;">
                <td>
                  <span class="device-id">{{ sim.description }}</span>
                </td>
                <td>
                  <span class="device-id">{{ sim.msisdn }}</span>
                </td>
                <td>
                  <span :class="[
                    'activity-badge',
                    'badge-' + sim.status
                  ]">
                    {{ sim.status }}
                  </span>
                </td>
                <td>
                  <div class="data-progress">
                    <div class="progress-bar-container">
                      <div class="progress-bar" :style="{ width: (sim.data_balance_in_mb / 200 * 100) + '%' }" :class="[
                        sim.data_balance_in_mb <= 10 ? 'critical' :
                          sim.data_balance_in_mb <= 50 ? 'warning' :
                            sim.data_balance_in_mb <= 100 ? 'medium' : 'good'
                      ]"></div>
                    </div>
                    <span class="progress-label">{{ sim.data_balance_in_mb.toFixed(1) }} MB</span>
                  </div>
                </td>
                <td>
                  <span :class="[
                    'activity-badge',
                    'badge-' + sim.alert_level
                  ]">
                    {{ sim.alert_level }}
                  </span>
                </td>
                <td>{{ sim.network }}</td>
                <td>
                  <span class="volume-display">{{ sim.DataUsedMb.toFixed(1) }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && sortedAndFilteredSims.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No SIMs Found</div>
            <div class="empty-description">
              Try adjusting your filters or refresh the data
            </div>
          </div>
        </div>

        <div class="pagination-container" v-if="!loading && sortedAndFilteredSims.length > 0">
          <!-- Page Size Selector -->
          <div class="page-size-selector">
            <label>Rows per page:</label>
            <select v-model.number="pageSize">
              <option v-for="size in [10, 20, 50, 100]" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>

          <!-- Info -->
          <div class="pagination-info">
            Showing {{ startIndex + 1 }}–{{ endIndex }} of {{ sortedAndFilteredSims.length }} SIMs
          </div>

          <!-- Buttons -->
          <div class="pagination-buttons">
            <button @click="prevPage" :disabled="currentPage === 1">Prev</button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button @click="nextPage" :disabled="currentPage === totalPages">Next</button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SimControlMainDashboard',
  data() {
    return {
      sims: [],
      pageSize: 20,
      currentPage: 1,
      stats: {
        total: 0,
        active: 0,
        inactive: 0,
        suspended: 0,
        criticalAlerts: 0,
        warningAlerts: 0,
        okCount: 0,
        criticalData: 0,
        lowData: 0,
        mediumData: 0,
        goodData: 0,
        totalDataUsedMb: 0,
        totalDatadata_balance_in_mb: 0,
        networks: {}
      },
      comparisonData: {
        previousTotal: 0,
        previousRemaining: 0,
        currentTotal: 0,
        currentRemaining: 0,
        dataUsed: 0,
        percentageUsed: 0
      },
      searchQuery: '',
      filterStatus: '',
      filterAlertLevel: '',
      sortBy: 'msisdn',
      loading: false,
      apiNotification: null,
      API_KEY: "sEYm9fsvSzYhYGXz0X59UmWCM1epshWe2ZctsFhCU3W-2KRlP2s0uW5FCS1LbKxQy7ROFJudawRWAobF9XwPbw",
      BASE_URL: "https://app.simcontrol.co.za/api",
      USE_MOCK_DATA: true,
      BUNDLE_CALC_METHOD: 'smart-estimate',
      STANDARD_BUNDLES: [120, 250, 500, 1000, 2000, 5000], // MB
      THRESHOLDS: {
        CRITICAL_DATA_MIN: 0,
        CRITICAL_DATA_MAX: 10,
        LOW_DATA_MIN: 10,
        LOW_DATA_MAX: 50,
        MEDIUM_DATA_MIN: 50,
        MEDIUM_DATA_MAX: 100,
        GOOD_DATA_MIN: 100
      }
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.sortedAndFilteredSims.length / this.pageSize);
    },

    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },

    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.sortedAndFilteredSims.length);
    },

    paginatedSims() {
      return this.sortedAndFilteredSims.slice(this.startIndex, this.endIndex);
    },
    filteredSims() {
      let filtered = this.sims;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(function (sim) {
          return sim.msisdn.toLowerCase().includes(query) ||
            sim.network.toLowerCase().includes(query) ||
            sim.description.toLowerCase().includes(query) ||
            sim.status.toLowerCase().includes(query);
        });
      }

      if (this.filterStatus) {
        const status = this.filterStatus;
        filtered = filtered.filter(function (sim) {
          return sim.status === status;
        });
      }

      if (this.filterAlertLevel) {
        const alertLevel = this.filterAlertLevel;
        filtered = filtered.filter(function (sim) {
          return sim.alert_level === alertLevel;
        });
      }

      return filtered;
    },
    sortedAndFilteredSims() {
      const sims = this.filteredSims.slice();
      const self = this;

      switch (this.sortBy) {
        case 'data-asc':
          return sims.sort(function (a, b) {
            return self.safeFloat(a.data_balance_in_mb) - self.safeFloat(b.data_balance_in_mb);
          });
        case 'data-desc':
          return sims.sort(function (a, b) {
            return self.safeFloat(b.data_balance_in_mb) - self.safeFloat(a.data_balance_in_mb);
          });
        case 'status':
          return sims.sort(function (a, b) {
            return a.status.localeCompare(b.status);
          });
        case 'msisdn':
        default:
          return sims.sort(function (a, b) {
            return a.msisdn.localeCompare(b.msisdn);
          });
      }
    }
  },
  methods: {
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    safeFloat(value) {
      var num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    },
    extractSimsFromResponse(data) {
      if (Array.isArray(data)) return data;
      if (typeof data === 'object' && data) {
        var keys = ['results', 'data', 'sims', 'items', 'payload'];
        for (var i = 0; i < keys.length; i++) {
          if (Array.isArray(data[keys[i]])) return data[keys[i]];
        }
        if ('id' in data || 'msisdn' in data) return [data];
      }
      return [];
    },
    async loadData() {
      var self = this;
      self.loading = true;

      // if (self.$refs.gridTable) {
      //   self.showLoadingOnElement("gridTable", 1, "radius");
      // }

      var allSims = [];
      var page = 1;
      var pageSize = 100;

      // Recursive paginated fetch using Promises
      function fetchPage() {
        return new Promise((resolve, reject) => {
          var onSuccess = function (response) {
            try {
              var data = response.data;
              var pageSims = self.extractSimsFromResponse(data);

              if (!pageSims || pageSims.length === 0) {
                return resolve(allSims);
              }

              allSims = allSims.concat(pageSims);

              // If this page is full, there might be more pages — fetch next
              if (pageSims.length === pageSize) {
                page++;
                fetchPage().then(resolve).catch(reject);
              } else {
                resolve(allSims);
              }
            } catch (err) {
              reject(err);
            }
          };

          var onFinally = function () {
            // if (self.$refs.gridTable && self.$refs.gridTable.$el) {
            //   self.$vs.loading.close(self.$refs.gridTable.$el);
            // }
          };

          // 👇 Wraps callback-based call
          try {
            self.$ajaxGet(
              self,
              `SimControl?page=${page}&page_size=${pageSize}`,
              onSuccess,
              onFinally
            );
          } catch (err) {
            reject(err);
          }
        });
      }

      try {
        // Wait for all pages to load
        const rawSims = await fetchPage();

        if (!rawSims || rawSims.length === 0) {
          throw new Error("No data available");
        }

        self.sims = self.enrichSims(rawSims);
        self.stats = self.analyzeData(self.sims);
        self.calculateComparison();
        var renderingDone = null;
        self.$nextTick(async function () {
          renderingDone = await self.initCharts();

        });

        if(renderingDone == "Done"){
          console.log("Charts rendering completed");
          self.apiNotification = {
            type: "success",
            message: `Successfully loaded ${rawSims.length} SIMs from SimControl API`,
          };

          // if (self.$refs.gridTable && self.$refs.gridTable.$el) {
          //   self.$vs.loading.close(self.$refs.gridTable.$el);
          // }
        }

      } catch (error) {
        console.error("Error loading data:", error);
        self.sims = [];
        self.stats = {
          total: 0,
          active: 0,
          inactive: 0,
          suspended: 0,
          criticalAlerts: 0,
          warningAlerts: 0,
          okCount: 0,
          criticalData: 0,
          lowData: 0,
          mediumData: 0,
          goodData: 0,
          totalDataUsedMb: 0,
          totalDatadata_balance_in_mb: 0,
          networks: {},
        };

        self.apiNotification = {
          type: "error",
          message: "Failed to load data: " + error.message,
        };
      } finally {
        self.loading = false;
        // if (self.$refs.gridTable && self.$refs.gridTable.$el) {
        //   self.$vs.loading.close(self.$refs.gridTable.$el);
        // }

        setTimeout(function () {
          self.apiNotification = null;
        }, 6000);
      }
    },
    // enrichSims(sims) {
    //   var enriched = [];
    //   for (var i = 0; i < sims.length; i++) {
    //     var s = sims[i];
    //     s.data_balance_in_mb = this.safeFloat(s.data_balance_in_mb);
    //     s.DataUsedMb = this.safeFloat(s.data_bundle_size_mb) - s.data_balance_in_mb;

    //     var mb = s.data_balance_in_mb;
    //     if (mb <= this.THRESHOLDS.CRITICAL_DATA_MAX) {
    //       s.alert_level = 'critical';
    //     } else if (mb <= this.THRESHOLDS.LOW_DATA_MAX) {
    //       s.alert_level = 'warning';
    //     } else {
    //       s.alert_level = 'ok';
    //     }

    //     var netMap = { 10: 'Vodacom', 11: 'MTN' };
    //     s.network = netMap[s.network_id] || 'Network ' + s.network_id;

    //     enriched.push(s);
    //   }
    //   return enriched;
    // },
    analyzeData(sims) {
      var total = sims.length;
      var active = 0;
      var inactive = 0;
      var suspended = 0;
      var criticalAlerts = 0;
      var warningAlerts = 0;
      var okCount = 0;
      var criticalData = 0;
      var lowData = 0;
      var mediumData = 0;
      var goodData = 0;
      var totalDataUsedMb = 0;
      var totalDatadata_balance_in_mb = 0;
      var totalEstimatedBundleMb = 0;
      var networks = {};

      for (var i = 0; i < sims.length; i++) {
        var s = sims[i];
        var status = (s.status || '').toLowerCase();
        if (status === 'active') active++;
        if (status === 'inactive') inactive++;
        if (status === 'suspended') suspended++;

        var alert = (s.alert_level || '').toLowerCase();
        if (alert === 'critical') criticalAlerts++;
        if (alert === 'warning') warningAlerts++;
        if (alert === 'ok') okCount++;

        var mb = this.safeFloat(s.data_balance_in_mb);
        if (mb >= this.THRESHOLDS.CRITICAL_DATA_MIN && mb <= this.THRESHOLDS.CRITICAL_DATA_MAX) {
          criticalData++;
        } else if (mb > this.THRESHOLDS.CRITICAL_DATA_MAX && mb <= this.THRESHOLDS.LOW_DATA_MAX) {
          lowData++;
        } else if (mb > this.THRESHOLDS.LOW_DATA_MAX && mb <= this.THRESHOLDS.MEDIUM_DATA_MAX) {
          mediumData++;
        } else if (mb > this.THRESHOLDS.MEDIUM_DATA_MAX) {
          goodData++;
        }

        totalDataUsedMb += this.safeFloat(s.DataUsedMb);
        totalDatadata_balance_in_mb += this.safeFloat(s.data_balance_in_mb);
        totalEstimatedBundleMb += this.safeFloat(s.EstimatedBundleSizeMb);

        var n = (s.network || 'unknown').toLowerCase();
        networks[n] = (networks[n] || 0) + 1;
      }

      return {
        total: total,
        active: active,
        inactive: inactive,
        suspended: suspended,
        criticalAlerts: criticalAlerts,
        warningAlerts: warningAlerts,
        okCount: okCount,
        criticalData: criticalData,
        lowData: lowData,
        mediumData: mediumData,
        goodData: goodData,
        totalDataUsedMb: Math.round(totalDataUsedMb * 10) / 10,
        totalDatadata_balance_in_mb: Math.round(totalDatadata_balance_in_mb * 10) / 10,
        totalEstimatedBundleMb: Math.round(totalEstimatedBundleMb * 10) / 10,
        networks: networks
      };
    },

    // ✅ Fixed calculateComparison to use estimated data
    calculateComparison() {
      var currentTotal = this.stats.totalEstimatedBundleMb ||
        (this.stats.totalDataUsedMb + this.stats.totalDatadata_balance_in_mb);
      var currentRemaining = this.stats.totalDatadata_balance_in_mb;
      var currentUsed = this.stats.totalDataUsedMb;

      // Simulate previous period (e.g., last week/month had 20% more remaining)
      var previousRemaining = currentRemaining * 1.2;
      var previousTotal = currentTotal;
      var dataUsed = previousRemaining - currentRemaining;
      var percentageUsed = previousTotal > 0 ? (dataUsed / previousTotal) * 100 : 0;

      this.comparisonData = {
        previousTotal: Math.round(previousTotal * 10) / 10,
        previousRemaining: Math.round(previousRemaining * 10) / 10,
        currentTotal: Math.round(currentTotal * 10) / 10,
        currentRemaining: Math.round(currentRemaining * 10) / 10,
        dataUsed: Math.round(dataUsed * 10) / 10,
        percentageUsed: Math.round(percentageUsed * 10) / 10
      };
    },
    refreshData() {
      var self = this;
      this.apiNotification = null;
      var wasUsingMock = this.USE_MOCK_DATA;
      this.USE_MOCK_DATA = false;

      this.getAllSims()
        .then(function (rawSims) {
          if (rawSims.length === 0) {
            self.apiNotification = {
              type: 'error',
              message: 'Failed to load data. Please check your connection and try again.'
            };
            self.USE_MOCK_DATA = true;
            return self.getAllSims();
          }
          return rawSims;
        })
        .then(function (rawSims) {
          if (rawSims.length > 0) {
            self.sims = self.enrichSims(rawSims);
            self.stats = self.analyzeData(self.sims);
            self.calculateComparison();

            self.$nextTick(function () {
              self.initCharts();
            });

            if (self.USE_MOCK_DATA && !wasUsingMock) {
              self.apiNotification = {
                type: 'warning',
                message: 'Live API unavailable. Using sample data. Check your internet connection or API credentials.'
              };
            } else if (!self.USE_MOCK_DATA) {
              self.apiNotification = {
                type: 'success',
                message: 'Successfully loaded data from SimControl API'
              };
            }
          }
        })
        .catch(function (error) {
          console.error('Refresh error:', error);
          self.apiNotification = {
            type: 'error',
            message: 'Failed to refresh data: ' + error.message
          };
        })
        .finally(function () {
          self.loading = false;
          setTimeout(function () {
            if (self.apiNotification) {
              self.apiNotification = null;
            }
          }, 6000);
        });
    },
    goToSimControl(id) {
      var simControlUrl = 'https://app.simcontrol.co.za/tecmo-automation/sims/' + id;
      window.open(simControlUrl);
    },
    // Replace your initCharts and chart methods with these fixed versions:

    initCharts() {
      return new Promise((resolve, reject) => {
        var self = this;

        // Wait for next tick to ensure DOM is ready
        self.$nextTick(() => {
          try {
            self.createDataDistributionChart();
            self.createStatusChart(); // ✅ Uncommented
            resolve("Done");
          } catch (error) {
            console.error("Chart initialization error:", error);
            reject(error);
          }
        });
      });
    },

    createDataDistributionChart() {
      var self = this;
      var canvas = this.$refs.dataDistributionChart;
      if (!canvas) {
        console.warn("Data distribution chart canvas not found");
        return;
      }

      var ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ✅ Fixed: Correct data array with matching colors
      var data = [
        self.stats.criticalData,   // 0-10MB
        self.stats.lowData,         // 10-50MB
        self.stats.mediumData,      // 50-100MB
        self.stats.goodData         // >100MB
      ];

      var labels = ['Critical (0-10MB)', 'Low (10-50MB)', 'Medium (50-100MB)', 'Good (>100MB)'];

      // ✅ Fixed: 4 colors for 4 segments
      var colors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];

      var total = data.reduce(function (a, b) { return a + b; }, 0);

      if (total === 0) {
        console.warn("No data available for chart");
        // Draw empty state
        ctx.fillStyle = '#e2e8f0';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('No data available', canvas.width / 2, canvas.height / 2);
        return;
      }

      var currentAngle = -Math.PI / 2; // Start at top
      var centerX = canvas.width / 2;
      var centerY = canvas.height / 2;
      var radius = Math.min(centerX, centerY) - 20;

      // Draw pie slices
      for (var i = 0; i < data.length; i++) {
        var value = data[i];
        if (value === 0) continue; // Skip empty segments

        var sliceAngle = (value / total) * 2 * Math.PI;

        ctx.fillStyle = colors[i];
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
        ctx.closePath();
        ctx.fill();

        // Add slice border
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        currentAngle += sliceAngle;
      }

      // Draw center circle (donut effect)
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.6, 0, 2 * Math.PI);
      ctx.fill();

      // Add center text
      ctx.fillStyle = '#2c3e50';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(total, centerX, centerY - 10);
      ctx.font = '12px sans-serif';
      ctx.fillText('Total SIMs', centerX, centerY + 10);
    },

    createStatusChart() {
      var self = this;
      var canvas = this.$refs.statusChart;

      if (!canvas) {
        console.warn("Status chart canvas not found");
        return;
      }

      var ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ✅ Fixed: Correct data array
      var data = [
        self.stats.active,
        self.stats.suspended,
        self.stats.inactive
      ];

      var labels = ['Active', 'Suspended', 'Inactive'];
      var colors = ['#22c55e', '#eab308', '#64748b'];

      var total = data.reduce(function (a, b) { return a + b; }, 0);

      if (total === 0) {
        ctx.fillStyle = '#e2e8f0';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('No data available', canvas.width / 2, canvas.height / 2);
        return;
      }

      var currentAngle = -Math.PI / 2; // Start at top
      var centerX = canvas.width / 2;
      var centerY = canvas.height / 2;
      var radius = Math.min(centerX, centerY) - 20;

      // Draw pie slices
      for (var i = 0; i < data.length; i++) {
        var value = data[i];
        if (value === 0) continue;

        var sliceAngle = (value / total) * 2 * Math.PI;

        ctx.fillStyle = colors[i];
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
        ctx.closePath();
        ctx.fill();

        // Add slice border
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        currentAngle += sliceAngle;
      }

      // Draw center circle (donut effect)
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.6, 0, 2 * Math.PI);
      ctx.fill();

      // Add center text
      ctx.fillStyle = '#2c3e50';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(total, centerX, centerY - 10);
      ctx.font = '12px sans-serif';
      ctx.fillText('Total', centerX, centerY + 10);
    },

    // ✅ UNIFIED enrichSims method - uses configurable approach
    enrichSims(sims) {
      var self = this;
      var enriched = [];

      for (var i = 0; i < sims.length; i++) {
        var s = sims[i];
        s.data_balance_in_mb = this.safeFloat(s.data_balance_in_mb);

        // Choose calculation method
        if (this.BUNDLE_CALC_METHOD === 'percentage-based') {
          // Percentage-based estimation
          var mb = s.data_balance_in_mb;
          if (mb <= this.THRESHOLDS.CRITICAL_DATA_MAX) {
            s.EstimatedBundleSizeMb = mb / 0.05; // 5% remaining
            s.UsagePercent = 95;
          } else if (mb <= this.THRESHOLDS.LOW_DATA_MAX) {
            s.EstimatedBundleSizeMb = mb / 0.20; // 20% remaining
            s.UsagePercent = 80;
          } else if (mb <= this.THRESHOLDS.MEDIUM_DATA_MAX) {
            s.EstimatedBundleSizeMb = mb / 0.40; // 40% remaining
            s.UsagePercent = 60;
          } else {
            s.EstimatedBundleSizeMb = mb / 0.70; // 70% remaining
            s.UsagePercent = 30;
          }
        } else {
          // Smart estimate - find closest standard bundle
          var remaining = s.data_balance_in_mb;
          var closestBundle = this.STANDARD_BUNDLES[0];

          for (var j = 0; j < this.STANDARD_BUNDLES.length; j++) {
            if (remaining <= this.STANDARD_BUNDLES[j]) {
              closestBundle = this.STANDARD_BUNDLES[j];
              break;
            }
          }

          // If remaining is very high, it might be a larger bundle
          if (remaining > this.STANDARD_BUNDLES[this.STANDARD_BUNDLES.length - 1] * 0.5) {
            closestBundle = this.STANDARD_BUNDLES[this.STANDARD_BUNDLES.length - 1];
          }

          s.EstimatedBundleSizeMb = closestBundle;
          s.UsagePercent = ((closestBundle - remaining) / closestBundle) * 100;
        }

        // Calculate data used
        s.EstimatedBundleSizeMb = Math.round(s.EstimatedBundleSizeMb * 10) / 10;
        s.DataUsedMb = Math.max(0, s.EstimatedBundleSizeMb - s.data_balance_in_mb);
        s.DataUsedMb = Math.round(s.DataUsedMb * 10) / 10;
        s.UsagePercent = Math.round((s.UsagePercent || 0) * 10) / 10;

        // Set alert level based on thresholds
        var mb = s.data_balance_in_mb;
        if (mb <= this.THRESHOLDS.CRITICAL_DATA_MAX) {
          s.alert_level = 'critical';
        } else if (mb <= this.THRESHOLDS.LOW_DATA_MAX) {
          s.alert_level = 'warning';
        } else {
          s.alert_level = 'ok';
        }

        // Map network ID to name
        var netMap = { 10: 'Vodacom', 11: 'MTN' };
        s.network = netMap[s.network_id] || 'Network ' + s.network_id;

        // Map network_status to status
        var statusMap = {
          'ACTIVE': 'active',
          'SUSPENDED': 'suspended',
          'INACTIVE': 'inactive'
        };
        s.status = !s.network_status ? "N/A" : statusMap[s.network_status] || s.network_status.toLowerCase();

        enriched.push(s);
      }
      return enriched;
    },
    showLoadingOnElement(element, scale, type) {
      var self = this;
      self.$vs.loading({
        type: type,
        container: self.$refs[element].$el,
        scale: scale
      });
    },
  },
  watch: {
    sortedAndFilteredSims() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.loadData();
  }
};
</script>

<style>
:root {
  --autumn-primary: #D2691E;
  --autumn-secondary: #CD853F;
  --autumn-accent: #FF8C00;
  --autumn-warm: #FFA500;
  --autumn-deep: #8B4513;
  --autumn-light: #FFE4B5;

  --fams-primary: #2C3E50;
  --fams-secondary: #3498DB;
  --fams-success: #27AE60;
  --fams-warning: #F39C12;
  --fams-danger: #E74C3C;
  --fams-dark: #34495E;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #e2e8f0;
  height: 100vh;
  overflow: hidden;
  width: 100%;
}

#app {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: none;
}

/* Header */
.header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.icon-pulse {
  font-size: 1.5rem;
  animation: pulse 2s infinite;
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.loading {
  background: #f39c12;
  animation: pulse-loading 1s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

@keyframes pulse-loading {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

/* API Notification */
.api-notification {
  padding: 12px 20px;
  border-bottom: 1px solid;
}

.api-notification.notification-success {
  background: #dcfce7;
  border-color: #86efac;
}

.api-notification.notification-warning {
  background: #fef3c7;
  border-color: #fde047;
}

.api-notification.notification-error {
  background: #fee2e2;
  border-color: #fca5a5;
}

.notification-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notification-message {
  font-size: 0.875rem;
  font-weight: 500;
}

.notification-success .notification-message {
  color: #14532d;
}

.notification-warning .notification-message {
  color: #78350f;
}

.notification-error .notification-message {
  color: #7f1d1d;
}

.notification-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.notification-close:hover {
  opacity: 1;
}

/* Main Content */
.main-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
  width: 100%;
  background: #e2e8f0;
  position: relative;
}

/* Statistics Section */
.stats-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.stats-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.stats-wrapper {
  padding: 16px 20px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #D2691E;
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  background: #ffffff;
}

.stat-label {
  color: #2c3e50;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50;
  font-weight: bold;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive {
  color: var(--fams-success);
}

.stat-change.negative {
  color: var(--fams-danger);
}

.stat-icon {
  font-size: 0.875rem;
}

/* Comparison Section */
.comparison-content {
  padding: 20px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.comparison-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.comparison-card.highlight {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe4b5 100%);
  border-color: #D2691E;
}

.comparison-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.comparison-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #34495E;
  margin-bottom: 12px;
}

.comparison-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.detail-label {
  font-size: 0.75rem;
  color: #64748b;
}

.detail-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.detail-value.primary {
  color: #3498DB;
}

.detail-value.accent {
  color: #D2691E;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.chart-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #cbd5e1;
  overflow: hidden;
}

.chart-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.chart-body {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.chart-legend {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #64748b;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend-dot.critical {
  background: #ef4444;
}

.legend-dot.warning {
  background: #f97316;
}

.legend-dot.medium {
  background: #eab308;
}

.legend-dot.good {
  background: #22c55e;
}

.legend-dot.active {
  background: #22c55e;
}

.legend-dot.suspended {
  background: #eab308;
}

.legend-dot.inactive {
  background: #64748b;
}

/* Search Section */
.search-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.search-controls-wrapper {
  padding: 16px 20px;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.search-group {
  display: flex;
  flex-direction: column;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input,
.search-select {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.search-input:focus,
.search-select:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
  background: #ffffff;
}

.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.apply-filters-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(210, 105, 30, 0.3);
}

/* Grid Container */
.grid-container {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 14px;
  background: white;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.action-button:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #D2691E;
  color: #D2691E;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 1rem;
}

/* Table */
.table-container {
  overflow: auto;
  position: relative;
  max-height: 500px;
  width: 100%;
  background: #ffffff;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid var(--autumn-light);
  border-top: 4px solid var(--autumn-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
}

.data-table tbody tr:hover {
  background: #f1f5f9;
  cursor: pointer;
}

.data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.data-table tbody tr:nth-child(even):hover {
  background: #f1f5f9;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: var(--fams-dark);
}

.device-id {
  font-weight: 600;
  color: var(--fams-dark);
}

.activity-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-active {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.badge-suspended {
  background: linear-gradient(135deg, #F39C12, #E67E22);
  color: white;
}

.badge-inactive {
  background: linear-gradient(135deg, #95A5A6, #7F8C8D);
  color: white;
}

.badge-critical {
  background: linear-gradient(135deg, #E74C3C, #C0392B);
  color: white;
}

.badge-warning {
  background: linear-gradient(135deg, #F39C12, #E67E22);
  color: white;
}

.badge-ok {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.data-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar-container {
  width: 80px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progress-bar.critical {
  background: #ef4444;
}

.progress-bar.warning {
  background: #f97316;
}

.progress-bar.medium {
  background: #eab308;
}

.progress-bar.good {
  background: #22c55e;
}

.progress-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.volume-display {
  font-weight: 600;
  color: var(--autumn-primary);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--fams-dark);
  background: #f8fafc;
  border-radius: 8px;
  margin: 20px;
  border: 1px solid #e2e8f0;
}

.empty-icon {
  font-size: 4rem;
  color: var(--autumn-light);
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

/* Pagination */
.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  background: #f1f5f9;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.per-page-info {
  color: #D2691E;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .search-controls {
    grid-template-columns: 1fr;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }
}
</style>
